<template>
  <div class="verification">
    <LanguageToggle 
      :currentLang="currentLang" 
      @change-lang="changeLang" 
    />
    <img 
      src="https://zc-open-media-mortdash2.s3.us-east-1.amazonaws.com/logos/allure_flat_logov2.webp"
      alt="International School Logo"
      class="school-logo"
    />
    <div class="content-wrapper">
      <h2>{{ t('title') }}</h2>
      <p class="subtitle">{{ t('welcome') }}</p>

      <!-- Mode Selection -->
      <div class="main-content" v-if="!selectedMode">
        <div class="mode-selection">
          <div class="mode-buttons">
            <button 
              class="mode-btn clock-in" 
              @click="selectMode('IN')"
            >
              <div class="btn-icon-wrapper">
                <i class="mdi mdi-login mode-icon"></i>
              </div>
              <div class="btn-content">
                <span class="btn-text">{{ t('clockIn') }}</span>
                <span class="btn-description">{{ t('clockInDesc') }}</span>
              </div>
            </button>
            <button 
              class="mode-btn clock-out" 
              @click="selectMode('OUT')"
            >
              <div class="btn-icon-wrapper">
                <i class="mdi mdi-logout mode-icon"></i>
              </div>
              <div class="btn-content">
                <span class="btn-text">{{ t('clockOut') }}</span>
                <span class="btn-description">{{ t('clockOutDesc') }}</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Recent Activity Section -->
        <RecentActivity :logs="recentLogs" />
      </div>

      <!-- Two Column Layout -->
      <div v-else class="main-content">
        <!-- Left Column: Camera and Controls -->
        <div class="camera-section">
          <div class="selected-mode">
            <div class="mode-text" :class="selectedMode.toLowerCase()">
              {{ selectedMode === 'IN' ? t('clockingIn') : t('clockingOut') }}
            </div>
            <button 
              class="cancel-btn" 
              @click="resetMode"
              v-if="!isProcessing"
            >
              <i class="fa-solid fa-times"></i>
              {{ t('cancel') }}
            </button>
          </div>

          <div class="camera-container">
            <div class="video-wrapper">
              <video 
                ref="video" 
                :width="640" 
                :height="480" 
                autoplay 
                playsinline
              ></video>
              
              <!-- Starting camera overlay -->
              <div class="camera-overlay" v-if="!isCameraStarted">
                <div class="camera-status">
                  {{ t('startingCamera') }}
                </div>
              </div>

              <!-- Warning overlay for clock in/out status -->
              <div class="camera-overlay warning" v-if="showClockOutOption || showClockInOption">
                <div class="warning-content">
                  <div class="warning-message">
                    {{ statusMessage }}
                  </div>
                  <div class="warning-actions">
                    <!-- <button 
                      v-if="showClockOutOption"
                      class="action-btn clock-out-btn"
                      @click="handleClockOutOption"
                      :disabled="isClockingOut"
                    >
                      <i class="mdi" :class="isClockingOut ? 'mdi-loading mdi-spin' : 'mdi-sign-out-alt'"></i>
                      {{ isClockingOut ? t('processing') : t('clockOutNow') }}
                    </button>
                    <button 
                      v-if="showClockInOption"
                      class="action-btn clock-in-btn"
                      @click="handleClockInOption"
                      :disabled="isClockingIn"
                    >
                      <i class="mdi" :class="isClockingIn ? 'mdi-loading mdi-spin' : 'mdi-sign-in-alt'"></i>
                      {{ isClockingIn ? t('processing') : t('clockInNow') }}
                    </button> -->
                    <button 
                      class="action-btn cancel-btn"
                      @click="resetMode"
                    >
                      <i class="fa-solid fa-times"></i>
                      {{ t('cancel') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Processing overlay - simplified -->
              <div class="camera-overlay processing" v-if="isProcessing">
                <div class="processing-status">
                  <div class="progress-circle">
                    <svg viewBox="0 0 36 36" class="circular-progress">
                      <path d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                        :stroke-dasharray="`${currentProgress}, 100`"
                      />
                    </svg>
                    <div class="progress-percentage">{{ Math.round(currentProgress) }}%</div>
                  </div>
                  <div class="processing-stage">{{ processingStage }}</div>
                </div>
              </div>

              <!-- Face guide - only show when not processing -->
              <div class="face-guide" v-if="isCameraStarted && !matchFound && !isProcessing">
                <div class="face-frame">
                  <div class="face-outline" :class="{
                    'good-position': facePositionOk || !faceDetector,
                    'bad-position': !facePositionOk && faceDetector && isCameraStarted
                  }"></div>
                </div>
                <div class="guide-text">
                  {{ faceDetector ? statusMessage : t('positionFace') }}
                </div>
              </div>
            </div>
            <canvas ref="canvas" style="display:none"></canvas>
          </div>

          <div class="status-container">
            <div class="status" :class="statusClass">
              {{ statusMessage }}
            </div>
          </div>
        </div>

        <!-- Recent Activity Section -->
        <RecentActivity :logs="recentLogs" />
      </div>

      <div class="success-dialog" v-if="showSuccessDialog">
        <div class="dialog-content">
          <div class="dialog-icon">
            <i class="fa-solid fa-circle-check success-icon"></i>
          </div>
          <h3>{{ selectedMode === 'IN' ? t('clockInSuccess') : t('clockOutSuccess') }}</h3>
          <p>{{ selectedMode === 'IN' ? t('welcomeUser', { name: successDialogMessage }) : t('goodbye', { name: successDialogMessage }) }}</p>
          <div class="countdown">
            <i class="fa-solid fa-clock"></i>
            {{ t('returningIn') }} {{ countdown }} {{ t('seconds') }}
          </div>
        </div>
      </div>

      <div class="verification-status" v-if="isProcessing">
        <div class="progress-bar">
          <div class="progress" :style="{ width: `${progress}%` }"></div>
        </div>
        <p class="status-text">{{ statusMessage }}</p>
      </div>

      <div class="error-dialog" v-if="showErrorDialog">
        <div class="dialog-content">
          <div class="dialog-icon">
            <i class="mdi mdi-alert-circle error-icon"></i>
          </div>
          <h3>Error</h3>
          <p>{{ errorDialogMessage }}</p>
          <div class="countdown">
            <i class="mdi mdi-clock-outline"></i>
            {{ t('returningIn') }} {{ countdown }} {{ t('seconds') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import translations from '@/locales/translations'
import LanguageToggle from '@/components/LanguageToggle.vue'
import RecentActivity from '@/components/RecentActivity.vue'
import axios from 'axios'
import moment from 'moment'
let faceDetector;
try {
  // Check if FaceDetector exists in window object
  if ('FaceDetector' in window) {
    faceDetector = new window.FaceDetector();
    console.log('FaceDetector API is supported');
  } else {
    console.warn('FaceDetector API is not supported in this browser');
    faceDetector = null;
  }
} catch (e) {
  console.warn('Error initializing FaceDetector:', e);
  faceDetector = null;
}

export default {
  name: 'VerificationView',
  components: {
    LanguageToggle,
    RecentActivity
  },
  data() {
    return {
      currentLang: localStorage.getItem('preferredLanguage') || 'en',
      selectedMode: null,  // 'IN' or 'OUT'
      stream: null,
      isProcessing: false,
      isCameraStarted: false,
      statusMessage: 'Select Clock In or Clock Out',
      statusClass: 'ready',
      lastAction: '',
      captureInterval: null,
      recentLogs: [],
      faceDetected: false,
      facePositionOk: false,
      faceDetector: faceDetector,
      matchFound: false,
      checkInterval: null,
      countdown: 0,
      processingStage: '', // Current stage of processing
      processingProgress: 0, // Progress percentage
      canStepAway: false, // Indicates if user can step away
      currentProgress: 0,
      targetProgress: 0,
      progressInterval: null,
      showClockOutOption: false,
      pendingClockOutStudent: null,
      lastCapturedImage: null,
      showClockInOption: false,
      pendingClockInStudent: null,
      showSuccessDialog: false,
      successDialogTitle: '',
      successDialogMessage: '',
      progress: 0,
      recentLogsPollingInterval: null,
      showErrorDialog: false,
      errorDialogMessage: '',
      isClockingIn: false,
      isClockingOut: false,
    }
  },
  async created() {
    // Replace localStorage loading with API call
    await this.fetchRecentActivity();
    
    // Set up polling to keep recent activity in sync across devices
    // this.recentLogsPollingInterval = setInterval(() => {
    //   this.fetchRecentActivity();
    // }, 30000); // Poll every 30 seconds
  },
  methods: {
    changeLang(lang) {
      this.currentLang = lang;
      localStorage.setItem('preferredLanguage', lang);
    },
    t(key, params = {}) {
      let text = translations[this.currentLang][key] || translations['en'][key] || key;
      // Replace any parameters in the text
      Object.keys(params).forEach(param => {
        text = text.replace(`{${param}}`, params[param]);
      });
      return text;
    },
    async selectMode(mode) {
      // Reset everything first
      this.resetMode();
      
      // Then set new mode and start
      this.selectedMode = mode;
      this.statusMessage = `Starting camera for ${mode === 'IN' ? 'clock in' : 'clock out'}...`;
      
      // Start camera with a small delay to ensure clean state
      setTimeout(async () => {
        await this.startCamera();
      }, 100);
    },

    resetMode() {
      // Stop all intervals and timers
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
      if (this.captureInterval) {
        clearInterval(this.captureInterval);
        this.captureInterval = null;
      }
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }

      // Stop camera stream
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }

      // Reset all states to initial values
      this.selectedMode = null;
      this.isCameraStarted = false;
      this.statusMessage = 'Select Clock In or Clock Out';
      this.statusClass = 'ready';
      this.isProcessing = false;
      this.faceDetected = false;
      this.facePositionOk = false;
      this.matchFound = false;
      this.canStepAway = false;
      
      // Reset all progress-related states
      this.processingStage = '';
      this.currentProgress = 0;
      this.processingProgress = 0;
      this.targetProgress = 0;

      // Reset video element
      if (this.$refs.video) {
        this.$refs.video.srcObject = null;
      }

      // Clear canvas
      if (this.$refs.canvas) {
        const ctx = this.$refs.canvas.getContext('2d');
        ctx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
      }

      // Reset clock out option states
      this.showClockOutOption = false;
      this.pendingClockOutStudent = null;

      // Reset clock in option states
      this.showClockInOption = false;
      this.pendingClockInStudent = null;

      // Clear stored image
      this.lastCapturedImage = null;

      // Reset error dialog
      this.showErrorDialog = false;
      this.errorDialogMessage = '';
    },

    async startCamera() {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'user',
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });
        
        const video = this.$refs.video;
        video.srcObject = this.stream;
        video.onloadedmetadata = () => {
          video.play();
          this.isCameraStarted = true;
          this.startCapturing();
          this.statusMessage = 'Ready to scan face...';
        };
      } catch (err) {
        console.error('Error accessing camera:', err);
        this.statusMessage = 'Camera error. Please check permissions.';
        this.statusClass = 'error';
      }
    },

    async checkFacePosition() {
      if (!faceDetector) {
        this.facePositionOk = true;
        this.statusMessage = 'Processing...';
        return true;
      }
      
      const video = this.$refs.video;
      try {
        const faces = await faceDetector.detect(video);
        
        if (faces.length === 0) {
          this.facePositionOk = false;
          this.statusMessage = 'No face detected. Please position your face in the frame.';
          return false;
        }
        
        const face = faces[0];
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        
        // Define the circular frame in the center
        const centerX = videoWidth / 2;
        const centerY = videoHeight / 2;
        const circleRadius = Math.min(videoWidth, videoHeight) * 0.25;
        
        const faceBox = face.boundingBox;
        const faceCenterX = faceBox.x + (faceBox.width / 2);
        const faceCenterY = faceBox.y + (faceBox.height / 2);
        
        // Calculate distance from face center to circle center
        const distance = Math.sqrt(
          Math.pow(faceCenterX - centerX, 2) + 
          Math.pow(faceCenterY - centerY, 2)
        );
        
        // Make the circle check more strict
        const faceInCircle = distance + (Math.max(faceBox.width, faceBox.height) / 2) <= circleRadius * 0.9;
        
        // Make size check more strict
        const idealFaceSize = circleRadius * 1.4;
        const faceSizeOk = 
          faceBox.width >= idealFaceSize * 0.85 &&
          faceBox.width <= idealFaceSize * 1.15;
        
        const previousPositionOk = this.facePositionOk;
        this.facePositionOk = faceInCircle && faceSizeOk;
        
        // Only update message if position status changed
        if (this.facePositionOk !== previousPositionOk || !this.facePositionOk) {
          if (!faceInCircle) {
            const angleRad = Math.atan2(faceCenterY - centerY, faceCenterX - centerX);
            const angleDeg = (angleRad * 180 / Math.PI + 360) % 360;
            
            if (distance > circleRadius) {
              if (angleDeg >= 315 || angleDeg < 45) {
                this.statusMessage = 'Move your face left';
              } else if (angleDeg >= 45 && angleDeg < 135) {
                this.statusMessage = 'Move your face up';
              } else if (angleDeg >= 135 && angleDeg < 225) {
                this.statusMessage = 'Move your face right';
              } else {
                this.statusMessage = 'Move your face down';
              }
            }
          } else if (!faceSizeOk) {
            this.statusMessage = faceBox.width < idealFaceSize * 0.85
              ? 'Please move closer to the camera'
              : 'Please move back from the camera';
          } else {
            this.statusMessage = 'Face position good! Hold still...';
          }
        }
        
        return this.facePositionOk;
      } catch (error) {
        console.warn('Face detection error:', error);
        this.facePositionOk = false;
        this.statusMessage = 'Face detection error. Please try again.';
        return false;
      }
    },

    startCapturing() {
      let stablePositionTimer = null;
      let stablePositionCount = 0;
      const REQUIRED_STABLE_FRAMES = 2; // Reduced from 3 to 2
      let isProcessingScheduled = false;
      let lastProcessingTime = 0;
      const PROCESSING_COOLDOWN = 2000; // 2 seconds cooldown between processing attempts

      // Check face position frequently
      this.checkInterval = setInterval(async () => {
        if (!this.isProcessing && !this.matchFound && !isProcessingScheduled) {
          const currentTime = Date.now();
          // Check if enough time has passed since last processing
          if (currentTime - lastProcessingTime < PROCESSING_COOLDOWN) {
            return;
          }

          const faceOk = await this.checkFacePosition();
          
          if (faceOk && this.facePositionOk) {
            stablePositionCount++;
            
            if (stablePositionCount >= REQUIRED_STABLE_FRAMES && !isProcessingScheduled) {
              isProcessingScheduled = true;
              this.statusMessage = 'Hold still, processing...';
              
              // Reduced delay before processing
              stablePositionTimer = setTimeout(() => {
                if (this.facePositionOk && !this.isProcessing) {
                  lastProcessingTime = Date.now();
                  this.processCurrentFrame();
                }
                isProcessingScheduled = false;
              }, 200); // Reduced from 500ms to 200ms
            }
          } else {
            stablePositionCount = 0;
            if (stablePositionTimer) {
              clearTimeout(stablePositionTimer);
              stablePositionTimer = null;
            }
            isProcessingScheduled = false;
          }
        }
      }, 300); // Adjusted from 200ms to 300ms for better performance

      this.captureInterval = null;
    },

    updateProgressSmooth(targetPercent) {
      const progress = Math.min(Math.max(0, Number(targetPercent.toFixed(1))), 100);
      this.currentProgress = progress;
      this.processingProgress = progress;
      
      // Always update the processing stage with current progress
      if (this.processingStage) {
        this.processingStage = `${this.processingStage.split('(')[0].trim()} (${Math.round(progress)}%)`;
      }
    },

    simulateProgress(start, end, duration, signal = null) {
      const actualStart = Math.abs(this.currentProgress - start) > 5 ? this.currentProgress : start;
      const step = (end - actualStart) * 0.1; // Increased step size
      const interval = 8; // Fixed very small interval
      let current = actualStart;
      
      return new Promise((resolve, reject) => {
        const progressInterval = setInterval(() => {
          if (current < end) {
            current = Math.min(current + Math.abs(step), end);
            this.updateProgressSmooth(current);
          } else if (current > end) {
            current = Math.max(current - Math.abs(step), end);
            this.updateProgressSmooth(current);
          } else {
            clearInterval(progressInterval);
            resolve();
          }
        }, interval);

        if (signal) {
          signal.addEventListener('abort', () => {
            clearInterval(progressInterval);
            reject(new Error('Progress simulation aborted'));
          });
        }
      });
    },

    startProgressPolling() {
      let currentAttempt = 0;
      const pollInterval = setInterval(() => {
        if (this.isProcessing) {
          currentAttempt++;
          // Calculate progress between 30-80% based on attempts
          const progress = Math.min(30 + Math.floor((currentAttempt / 15) * 50), 80);
          this.updateProgressSmooth(progress);
        } else {
          clearInterval(pollInterval);
        }
      }, 0);

      return pollInterval;
    },

    async processCurrentFrame() {
      if (this.isProcessing || this.matchFound) return;
      
      if (!this.facePositionOk) {
        return;
      }
      
      this.isProcessing = true;
      
      const canvas = this.$refs.canvas;
      const video = this.$refs.video;
      
      try {
        // Start with initial progress
        this.processingStage = 'Initializing...';
        await this.simulateProgress(0, 5, 500);

        // Capture the image first
        if (faceDetector) {
          const faces = await faceDetector.detect(video);
          if (faces.length === 0) {
            this.isProcessing = false;
            return;
          }

          const face = faces[0];
          const faceBox = face.boundingBox;

          // Reduced padding around the face
          const padding = {
            x: faceBox.width * 0.1, // Reduced from 0.2 to 0.1
            y: faceBox.height * 0.1 // Reduced from 0.2 to 0.1
          };

          // Reduced image size for faster processing
          canvas.width = 300;  // Reduced from 500 to 300
          canvas.height = 300; // Reduced from 500 to 300
          const ctx = canvas.getContext('2d');

          // Calculate the source rectangle
          const sourceX = Math.max(0, faceBox.x - padding.x);
          const sourceY = Math.max(0, faceBox.y - padding.y);
          const sourceWidth = Math.min(faceBox.width + (padding.x * 2), video.videoWidth - sourceX);
          const sourceHeight = Math.min(faceBox.height + (padding.y * 2), video.videoHeight - sourceY);

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(
            video,
            sourceX, sourceY, sourceWidth, sourceHeight,
            0, 0, canvas.width, canvas.height
          );
          ctx.setTransform(1, 0, 0, 1, 0, 0);
        } else {
          // Fallback with optimized size
          canvas.width = 300;
          canvas.height = 300;
          const ctx = canvas.getContext('2d');
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        }

        this.processingStage = 'Preparing image';
        // await this.simulateProgress(5, 15, 500);
        const imageData = canvas.toDataURL('image/jpeg', 0.7);

        // Store the image for potential clock out
        this.lastCapturedImage = imageData;

        this.processingStage = 'Starting verification';
        console.log('Starting verification:', new Date().toLocaleTimeString());
        // await this.simulateProgress(15, 20, 500);
        this.canStepAway = true;

        // Create progress controller
        // const abortController = new AbortController();
        
        // Start continuous progress simulation
        // const simulateVerificationProgress = () => {
        //   return this.simulateProgress(20, 75, 15000, abortController.signal);
        // };

        // const progressPromise = simulateVerificationProgress();
        
        try {
          const response = await fetch(`${process.env.VUE_APP_API_URL}/api/verify`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              image: imageData,
              mode: this.selectedMode
            })
          });

          // Stop the progress simulation
          // abortController.abort();
          // await progressPromise.catch(() => {});

          const result = await response.json();
          
          if (result.match) {
            console.log('Match found:', new Date().toLocaleTimeString());
            this.processingStage = 'Completing verification';
            await this.simulateProgress(this.currentProgress, 90, 500);
            this.processingStage = 'Verification complete';
            await this.simulateProgress(90, 100, 300);
            await this.handleSuccessfulMatch(result);
          } else {
            if (result.error === 'Student not found') {
              // Stop processing immediately
              this.isProcessing = false;
              this.matchFound = true; // Prevent further processing
              
              // Show error dialog
              this.errorDialogMessage = 'Student not found';
              this.showErrorDialog = true;
              this.playSound('error');
              
              // Start countdown and reset after
              this.countdown = 3;
              const countdownInterval = setInterval(() => {
                this.countdown--;
                if (this.countdown <= 0) {
                  clearInterval(countdownInterval);
                  this.showErrorDialog = false;
                  this.resetMode(); // Return to main menu
                }
              }, 1000);
              
              return; // Exit the method
            } else {
              this.processingStage = 'Verification failed';
              await this.simulateProgress(this.currentProgress, 0, 800);
              this.canStepAway = false;
              this.statusMessage = result.error || 'Face not recognized, trying again...';
              this.statusClass = 'error';
            }
          }

        } catch (error) {
          console.error('Verification error:', error);
          this.statusMessage = 'Verification failed';
          this.statusClass = 'error';
          this.processingStage = 'Error';
          await this.simulateProgress(this.currentProgress, 0, 800);
          this.canStepAway = false;
        } finally {
          this.isProcessing = false;
        }

      } catch (err) {
        console.error('Verification error:', err);
        this.statusMessage = 'Verification failed';
        this.statusClass = 'error';
        this.processingStage = 'Error';
        await this.simulateProgress(this.currentProgress, 0, 800);
        this.canStepAway = false;
      }
    },

    async handleSuccessfulMatch(result) {
      console.log('Verify response:', result);
      this.matchFound = true;
      
      // Stop all intervals
      if (this.checkInterval) clearInterval(this.checkInterval);
      if (this.captureInterval) clearInterval(this.captureInterval);

      try {
        // Check student's current status
        const currentStatus = await this.checkStudentStatus(result.studentId);
        
        // If trying to clock in but already clocked in
        if (this.selectedMode === 'IN' && currentStatus === 'IN') {
          this.statusMessage = this.t('alreadyClockedIn');
          this.statusClass = 'warning';
          this.playSound('warning');
          
          // Add clock out option
          this.showClockOutOption = true;
          this.pendingClockOutStudent = {
            name: result.studentName,
            id: result.studentId
          };
          return;
        }
        
        // If trying to clock out but already clocked out
        if (this.selectedMode === 'OUT' && currentStatus === 'OUT') {
          this.statusMessage = this.t('needsClockIn');
          this.statusClass = 'warning';
          this.playSound('warning');
          
          // Add clock in option
          this.showClockInOption = true;
          this.pendingClockInStudent = {
            name: result.studentName,
            id: result.studentId
          };
          return;
        }

        // If status matches intended action, proceed with normal flow
        try {
          // Call EDClock API directly (no need for local attendance)
          await this.callEdClockAPI(result.studentId, this.selectedMode);
          
          // Show success dialog
          this.successDialogMessage = result.studentName;
          this.showSuccessDialog = true;
          this.playSound('success');
          
          // Update recent activity
          await this.fetchRecentActivity();
          
          // Start countdown for auto-reset
          this.startCountdown();

        } catch (error) {
          console.error('Attendance error:', error);
          this.statusMessage = this.selectedMode === 'IN' ? 'Failed to clock in' : 'Failed to clock out';
          this.statusClass = 'error';
          this.playSound('error');
        }

      } catch (error) {
        console.error('Status check error:', error);
        this.statusMessage = 'Failed to verify status';
        this.statusClass = 'error';
        this.playSound('error');
      }
    },

    async handleClockOutOption() {
      if (this.isClockingOut) return;
      
      this.isClockingOut = true;
      try {
        // Remove local attendance creation
        // Only call EDClock API
        await this.callEdClockAPI(this.pendingClockOutStudent.id, 'OUT');

        // Update recent activity
        await this.fetchRecentActivity();

        // Show success dialog
        this.successDialogMessage = this.pendingClockOutStudent.name;
        this.selectedMode = 'OUT';
        this.showSuccessDialog = true;
        this.playSound('success');
        
        // Start countdown for auto-reset
        this.startCountdown();

        // Clear the warning overlay
        this.showClockOutOption = false;
        this.pendingClockOutStudent = null;

      } catch (error) {
        console.error('Clock out error:', error);
        this.statusMessage = 'Failed to clock out';
        this.statusClass = 'error';
        this.playSound('error');
      } finally {
        this.isClockingOut = false;
      }
    },

    async handleClockInOption() {
      if (this.isClockingIn) return;
      
      this.isClockingIn = true;
      try {
        // Remove local attendance creation
        // Only call EDClock API
        await this.callEdClockAPI(this.pendingClockInStudent.id, 'IN');

        // Update recent activity
        await this.fetchRecentActivity();

        // Show success dialog
        this.successDialogMessage = this.pendingClockInStudent.name;
        this.selectedMode = 'IN';
        this.showSuccessDialog = true;
        this.playSound('success');
        
        // Start countdown for auto-reset
        this.startCountdown();

        // Clear the warning overlay
        this.showClockInOption = false;
        this.pendingClockInStudent = null;

      } catch (error) {
        console.error('Clock in error:', error);
        this.statusMessage = 'Failed to clock in';
        this.statusClass = 'error';
        this.playSound('error');
      } finally {
        this.isClockingIn = false;
      }
    },

    // Update callEdClockAPI to handle explicit mode
    async callEdClockAPI(studentId, mode = null) {
      const clockMode = mode || this.selectedMode;
      const edClockUrl = clockMode === 'IN'
        ? `${process.env.VUE_APP_API_EDCLOCK}/api/v1/edclock/scan-qr-code-v2?type=student&student_id=${studentId}`
        : `${process.env.VUE_APP_API_EDCLOCK}/api/v1/edclock/toggle-clockout?type=student&student_id=${studentId}`;

      const edClockResponse = await fetch(edClockUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (edClockResponse.ok) {
        const edClockResult = await edClockResponse.json();
        console.log(`EDClock ${clockMode.toLowerCase()} response:`, edClockResult);
      } else {
        throw new Error(await edClockResponse.text());
      }
    },

    async updateLogs(result) {
      this.lastAction = `${result.studentName} ${result.action} at ${new Date().toLocaleTimeString()}`;
      
      // Fetch latest logs instead of managing locally
      await this.fetchRecentActivity();
    },

    startCountdown() {
      this.countdown = 3;
      const countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(countdownInterval);
          this.showSuccessDialog = false;
          this.resetMode();
        }
      }, 1000);
    },

    formatTime(time) {
      if (!time) return '---';
      return moment(time).format('hh:mm A');
    },

    playSound(type) {
      // You can implement sound effects here
      const audio = new Audio(type === 'success' ? '/sounds/success.mp3' : '/sounds/error.mp3');
      audio.play().catch(err => console.log('Sound not played:', err));
    },

    startNewVerification() {
      // First reset everything
      this.resetMode();
      
      // Then reselect the current mode
      const currentMode = this.selectedMode;
      setTimeout(() => {
        this.selectMode(currentMode);
      }, 100); // Small delay to ensure clean state
    },

    updateStatus(message, progress) {
      this.statusMessage = message;
      this.progress = progress;
    },

    async verifyFace(imageData) {
      this.isProcessing = true;
      this.progress = 0;
      
      try {
        // Start with 30% progress immediately
        this.updateStatus('Processing...', 30);
        
        const response = await fetch('/api/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            image: imageData
          })
        });

        // Jump to 70% as soon as we get response
        this.updateStatus('Analyzing results...', 70);
        const result = await response.json();

        if (result.match) {
          // Immediately jump to 100% on successful match
          this.progress = 100;
          this.updateStatus(`Match found! (${result.performance?.totalTime}s)`, 100);
          // Handle success
          this.handleSuccess(result);
        } else {
          this.updateStatus(result.error || 'No match found', 0);
          // Handle no match
          this.handleNoMatch(result);
        }

      } catch (error) {
        this.updateStatus('Verification failed', 0);
        console.error('Verification error:', error);
      } finally {
        // Only reset if no match was found
        if (!this.matchFound) {
          setTimeout(() => {
            this.isProcessing = false;
            this.progress = 0;
          }, 500);
        }
      }
    },

    // Add new method to fetch recent activity
    async fetchRecentActivity() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_EDCLOCK}/api/v1/edclock/get-last-10-time-records`);
        
        if (response.data.success) {
          // Transform the data to show each record as a single entry with both clock in and out
          this.recentLogs = response.data.data.map(record => ({
            studentName: record.student,
            clockIn: record.clock_in_actual ? moment.utc(record.clock_in_actual).local() : null,
            clockOut: record.clock_out_actual ? moment.utc(record.clock_out_actual).local() : null
          }));

          // Sort by clock in time descending
          this.recentLogs.sort((a, b) => {
            if (!a.clockIn) return 1;
            if (!b.clockIn) return -1;
            return b.clockIn - a.clockIn;
          });
        } else {
          console.error('Failed to fetch recent activity:', response.data.message);
        }
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      }
    },

    // Add this new method to check student status
    async checkStudentStatus(studentId) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_EDCLOCK}/api/v1/edclock/get-student-is-clock-in-or-clock-out`, {
            params: { student_id: studentId }
          }
        );

        if (response.data.success) {
          return response.data.data; // This will be "IN" or "OUT"
        }
        throw new Error(response.data.message || 'Failed to get student status');
      } catch (error) {
        console.error('Error checking student status:', error);
        throw error;
      }
    },

    getInitials(name) {
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
  },

  beforeUnmount() {
    // Clear the polling interval
    if (this.recentLogsPollingInterval) {
      clearInterval(this.recentLogsPollingInterval);
    }
    
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    if (this.captureInterval) {
      clearInterval(this.captureInterval);
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }
}
</script>

<style scoped>
.verification {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.content-wrapper {
  width: 100%;
  max-width: 1200px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

h2 {
  font-size: 2.5rem;
  color: #1e293b;
  margin-bottom: 0.5rem;
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.5px;
}

.subtitle {
  text-align: center;
  color: #64748b;
  font-size: 1.1rem;
  margin-bottom: 3rem;
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.mode-selection {
  background: white;
  padding: 2rem;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.03);
}

.mode-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.mode-btn {
  padding: 2rem;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 2rem;
  background: white;
  color: #1e293b;
  border: 2px solid transparent;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  text-align: left;
}

.btn-icon-wrapper {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.mode-icon {
  font-size: 2.5rem;
  transition: transform 0.3s ease;
}

.btn-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.btn-text {
  font-size: 1.4rem;
  font-weight: 600;
}

.btn-description {
  font-size: 1rem;
  color: #64748b;
}

.mode-btn.clock-in .btn-icon-wrapper {
  background: #42b98315;
  color: #42b983;
}

.mode-btn.clock-in:hover {
  border-color: #42b983;
  transform: translateY(-2px);
  box-shadow: 0 15px 30px rgba(66, 185, 131, 0.15);
}

.mode-btn.clock-out .btn-icon-wrapper {
  background: #ff980015;
  color: #ff9800;
}

.mode-btn.clock-out:hover {
  border-color: #ff9800;
  transform: translateY(-2px);
  box-shadow: 0 15px 30px rgba(255, 152, 0, 0.15);
}

.recent-activity {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
  overflow: hidden;
}

.section-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-title i {
  font-size: 1.25rem;
  color: #64748b;
}

.header-title h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.log-list {
  padding: 0.5rem;
}

.log-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.header-cell {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.log-item {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  align-items: center;
  transition: background-color 0.2s ease;
}

.log-item:hover {
  background-color: #f8fafc;
}

.student-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.avatar {
  width: 2rem;
  height: 2rem;
  background: #e2e8f0;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
}

.student-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 0.875rem;
}

.time-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #475569;
  font-size: 0.875rem;
}

.time-cell i {
  font-size: 0.875rem;
  opacity: 0.5;
}

.time-cell:nth-child(2) i {
  color: #10b981;
}

.time-cell:nth-child(3) i {
  color: #f97316;
}

.no-logs {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #64748b;
  text-align: center;
}

.no-logs i {
  font-size: 2rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-logs p {
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
}

@media (max-width: 768px) {
  .section-header {
    padding: 1rem;
  }

  .log-header {
    display: none;
  }

  .log-item {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1rem;
    border-bottom: 1px solid #e2e8f0;
  }

  .log-item:last-child {
    border-bottom: none;
  }

  .time-cell {
    padding-left: 2.75rem;
  }

  .mode-buttons {
    grid-template-columns: 1fr;
  }
}

.camera-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.selected-mode {
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.camera-container {
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.video-wrapper {
  position: relative;
  width: 100%;
  max-width: 640px;
  height: 480px;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  margin: 0 auto;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}

video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
}

.mode-text {
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.mode-text.in {
  color: #42b983;
}

.mode-text.out {
  color: #ff9800;
}

.cancel-btn {
  padding: 0.4rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 500;
}

.cancel-btn:hover {
  background: #f1f5f9;
  color: #475569;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.face-guide {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.face-frame {
  position: relative;
  width: 280px;
  height: 340px;
  border: 3px solid rgba(255, 255, 255, 0.9);
  border-radius: 24px;
  background: transparent;
  box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.5);
  margin: 1rem;
}

.face-outline {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 180px;
  height: 180px;
  border: 3px dashed rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.face-outline.good-position {
  border-style: solid;
  border-color: #42b983;
  box-shadow: 0 0 20px rgba(66, 185, 131, 0.3);
}

.face-outline.bad-position {
  border-color: #ff4444;
  box-shadow: 0 0 20px rgba(255, 68, 68, 0.3);
}

.guide-text {
  margin-top: 2rem;
  padding: 0.75rem 1.5rem;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 100px;
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
}

.camera-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
}

.camera-status {
  color: white;
  font-size: 1.2rem;
  font-weight: 500;
  text-align: center;
}

.processing-status {
  text-align: center;
  color: white;
}

.progress-circle {
  width: 120px;
  height: 120px;
  margin: 0 auto 1rem;
  position: relative;
}

.circular-progress {
  transform: rotate(-90deg);
  width: 100%;
  height: 100%;
}

.circular-progress path {
  fill: none;
  stroke: #42b983;
  stroke-width: 3;
  stroke-linecap: round;
  transition: stroke-dasharray 0.3s ease;
}

.progress-percentage {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.8rem;
  font-weight: 600;
  color: #42b983;
}

.processing-stage {
  font-size: 1.2rem;
  font-weight: 500;
  margin-top: 1rem;
  color: white;
}

@media (max-width: 768px) {
  .camera-section {
    margin: 0;
  }

  .camera-container {
    padding: 1rem;
    border-radius: 0;
  }

  .video-wrapper {
    width: 100%;
    height: calc(100vw * 0.75);
    max-height: 480px;
    border-radius: 0;
  }

  .camera-overlay {
    border-radius: 0;
  }

  .face-frame {
    width: 240px;
    height: 300px;
  }
}

.status-container {
  background: white;
  padding: 1rem;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.status {
  padding: 1.25rem;
  border-radius: 16px;
  font-size: 1.2rem;
  font-weight: 500;
  text-align: center;
  transition: all 0.3s ease;
}

.status:hover {
  transform: scale(1.02);
}

.status-section {
  background: white;
  padding: 1rem;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.03);
}

.recent-logs {
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.03);
  margin: 0;
  flex-grow: 1;
  max-height: 600px;
  overflow-y: auto;
}

.recent-logs h3 {
  font-size: 1.2rem;
  color: #1e293b;
  margin-bottom: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.log-item {
  padding: 0.75rem 1rem;
  border-radius: 12px;
  margin-bottom: 0.5rem;
  background: #f8fafc;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
  align-items: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.log-item:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.student-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.action-type {
  padding: 0.4rem 0.8rem;
  border-radius: 30px;
  font-size: 0.8rem;
  text-align: center;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.action-type.in {
  background: #42b98315;
  color: #42b983;
}

.action-type.out {
  background: #ff980015;
  color: #ff9800;
}

.timestamp {
  color: #666;
  font-size: 0.9rem;
  text-align: right;
}

.success-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  z-index: 9999;
}

.dialog-content {
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  transform: translateY(0);
}

.dialog-icon {
  margin-bottom: 1.5rem;
}

.success-icon {
  font-size: 4rem;
  color: #42b983;
  animation: bounceIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.dialog-content h3 {
  font-size: 1.8rem;
  color: #1e293b;
  margin-bottom: 1rem;
  font-weight: 600;
}

.dialog-content p {
  font-size: 1.2rem;
  color: #4a5568;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.dialog-content .countdown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #718096;
  font-size: 1rem;
  font-weight: 500;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounceIn {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

html {
  scroll-behavior: smooth;
}

.back-btn,
.clock-out-btn,
.clock-in-btn,
.new-verification-btn {
  position: relative;
  overflow: hidden;
}

.back-btn::after,
.clock-out-btn:hover::after,
.clock-in-btn:hover::after,
.new-verification-btn:hover::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.6s ease, height 0.6s ease;
}

.back-btn:hover::after,
.clock-out-btn:hover::after,
.clock-in-btn:hover::after,
.new-verification-btn:hover::after {
  width: 300px;
  height: 300px;
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
}

.mode-btn {
  animation: float 4s ease-in-out infinite;
}

.mode-btn:nth-child(2) {
  animation-delay: 2s;
}

@media (max-width: 1024px) {
  .two-column-layout {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .right-column {
    order: 1;
  }

  .left-column {
    order: 2;
  }
}

/* Add custom scrollbar for the log list */
.log-list::-webkit-scrollbar {
  width: 8px;
}

.log-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.log-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.log-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.school-logo {
  position: fixed;
  top: 1rem;
  left: 1rem;
  width: 150px;
  height: auto;
  object-fit: contain;
  z-index: 1000;
}

.verification-status {
  margin: 20px 0;
  text-align: center;
  background: white;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}

.progress {
  height: 100%;
  background: #4f46e5;
  transition: width 0.5s ease-out; /* Smoother transition */
}

.status-text {
  margin-top: 8px;
  color: #4b5563;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Remove the pulse animation */
.processing {
  /* animation removed */
  opacity: 1;
}

.camera-overlay.warning {
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.warning-content {
  background: rgba(255, 255, 255, 0.1);
  padding: 2rem;
  border-radius: 16px;
  text-align: center;
  backdrop-filter: blur(8px);
  max-width: 80%;
}

.warning-message {
  color: white;
  font-size: 1.4rem;
  font-weight: 500;
  margin-bottom: 2rem;
  line-height: 1.4;
}

.warning-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center; /* Add this to ensure vertical alignment */
}

.action-btn {
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 160px;
  max-width: 160px;
  justify-content: center;
  height: 45px;
  white-space: nowrap;
}

.action-btn.clock-in-btn,
.action-btn.clock-out-btn,
.action-btn.cancel-btn {
  padding: 0 1.5rem;
  line-height: 1;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.action-btn i {
  font-size: 1.2rem;
}

.error-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  z-index: 9999;
}

.error-dialog .dialog-content {
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.error-dialog .error-icon {
  font-size: 4rem;
  color: #ef4444;
  animation: bounceIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.error-dialog h3 {
  font-size: 1.8rem;
  color: #1e293b;
  margin-bottom: 1rem;
  font-weight: 600;
}

.error-dialog p {
  font-size: 1.2rem;
  color: #4a5568;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.error-dialog .countdown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #718096;
  font-size: 1rem;
  font-weight: 500;
}

.error-dialog .countdown i {
  font-size: 1.2rem;
}

.action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.action-btn:disabled:hover {
  transform: none;
  box-shadow: none;
}

.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Add/update these responsive styles */
@media (max-width: 1024px) {
  .content-wrapper {
    padding: 1rem;
    margin-top: 4rem; /* Add space for logo */
  }

  h2 {
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    font-size: 1rem;
    margin-bottom: 2rem;
  }

  .school-logo {
    width: 120px; /* Smaller logo for mobile */
    top: 0.5rem;
  }

  .main-content {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .mode-btn {
    padding: 1.25rem;
    gap: 1rem;
  }

  .btn-icon-wrapper {
    width: 48px;
    height: 48px;
  }

  .mode-icon {
    font-size: 2rem;
  }

  .btn-text {
    font-size: 1.2rem;
  }

  .btn-description {
    font-size: 0.9rem;
  }

  /* Camera view adjustments */
  .video-wrapper {
    width: 100%;
    height: auto;
    aspect-ratio: 4/3;
  }

  video {
    width: 100%;
    height: 100%;
  }

  .face-frame {
    width: 220px;
    height: 280px;
  }

  .face-outline {
    width: 140px;
    height: 140px;
  }

  .guide-text {
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
  }

  /* Warning overlay adjustments */
  .warning-content {
    padding: 1.5rem;
    max-width: 90%;
  }

  .warning-message {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }

  .warning-actions {
    flex-direction: column;
    gap: 0.75rem;
  }

  .action-btn {
    width: 100%;
    max-width: none;
    min-width: auto;
  }

  /* Dialog adjustments */
  .dialog-content {
    padding: 1.5rem;
    width: 85%;
  }

  .success-icon,
  .error-icon {
    font-size: 3rem;
  }

  .dialog-content h3 {
    font-size: 1.5rem;
  }

  .dialog-content p {
    font-size: 1rem;
  }

  /* Recent activity adjustments */
  .log-item {
    padding: 0.6rem 0.8rem;
    grid-template-columns: 1.5fr 1fr 1fr;
    gap: 0.5rem;
  }

  .student-name {
    font-size: 0.9rem;
  }

  .action-type {
    padding: 0.3rem 0.6rem;
    font-size: 0.75rem;
    min-width: 70px;
  }

  .timestamp {
    font-size: 0.8rem;
    min-width: 70px;
  }

  /* Add these new styles for two-column layout in mobile */
  .two-column-layout {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .left-column {
    order: -1; /* This moves the camera section to the top */
  }

  .right-column {
    order: 2;
  }

  /* Adjust camera container for mobile */
  .camera-container {
    margin: 0 -1rem;
    border-radius: 0;
    padding: 0;
  }

  .video-wrapper {
    border-radius: 0;
    width: 100vw;
    max-width: 100vw;
  }

  /* Adjust the selected mode indicator */
  .selected-mode {
    margin: 0 0 0.5rem 0;
    border-radius: 0;
  }

  /* Adjust status container */
  .status-container {
    margin: 0.5rem 0;
    border-radius: 12px;
  }
}

/* Additional adjustments for very small screens */
@media (max-width: 380px) {
  .school-logo {
    width: 100px;
  }

  h2 {
    font-size: 1.5rem;
  }

  .btn-text {
    font-size: 1.1rem;
  }

  .face-frame {
    width: 200px;
    height: 260px;
  }

  .face-outline {
    width: 120px;
    height: 120px;
  }

  .log-item {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .timestamp {
    text-align: center;
  }
}

/* Fix for iOS Safari viewport height issue */
@supports (-webkit-touch-callout: none) {
  .verification {
    min-height: -webkit-fill-available;
  }
}
</style> 